import { useCallback, useMemo, useState } from 'react';

import {
  useApolloClient,
  useFragment as useApolloFragment,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { Box, Skeleton, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { DOC_TEMPLATE_THEMES } from '@modules/_shared/common/queries';
import { useAddUserModal } from '@modules/_shared/components/AddUserModal';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { UPDATE_LEAD_CONTACT } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import {
  DOC_TEMPLATES_CMA_REPORT,
  GET_CMA_REPORT_BROKER_SELECTIONS,
  STEP_COVER_PHOTO_CMA_FRAGMENT,
  UPDATE_TEAM_BRANDING_COLOR_CMA,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import CoverPhotoSelector from '@modules/reports/shared/CoverPhotoSelector/CoverPhotoSelector';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { ReportSettingsSection } from '@modules/reports/shared/ReportSettingsSection';
import { useRefetchWhenMissing } from '@modules/reports/shared/useRefetchWhenMissing';
import {
  getFilteredPropertyImages,
  useUpdateCmaReport,
} from '@modules/reports/shared/utils';
import { GOOGLE_MAPS_TOKEN } from '@shared/config';
import { useDebouncedHandler } from '@shared/hooks/debounce';

const getSatelliteImageUrl = (lat: number, lng: number) => {
  return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=16&size=400x200&maptype=satellite&markers=color:red|${lat},${lng}&style=feature:poi|element:labels|visibility:off&scale=2&key=${GOOGLE_MAPS_TOKEN}&channel=crm`;
};

type StepCoverForm = {
  report_title: string;
  broker_id: string;
  contact_id: string;
  font_family: string | null;
  doc_template_id: string;
  doc_template_theme_id: string | null;
  language: string;
  branding_color: string | null;
};

const defaultTitles = {
  en: 'Valuation analysis',
  fr: 'Avis de valeur',
  de: 'Vergleichende Marktanalyse',
  it: 'Analisi di valutazione',
  es: 'Informe de valoración',
  pl: 'Raport CMA',
};

const StepCoverPhoto = (props: ReportComponentProps) => {
  const { reportId: cmaReportId } = props;
  const { setError } = useReportContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const { complete, data } = useApolloFragment({
    fragment: STEP_COVER_PHOTO_CMA_FRAGMENT,
    fragmentName: 'StepCoverPhotoCma',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const { data: docTemplatesData, refetch: refetchDocTemplates } = useQuery(
    DOC_TEMPLATES_CMA_REPORT,
  );

  const currentTemplateId = data?.doc_template?.id;
  useRefetchWhenMissing(
    currentTemplateId,
    docTemplatesData?.doc_templates,
    refetchDocTemplates,
    setError,
  );

  const { data: themeTemplatesData, refetch: refetchThemeTemplates } = useQuery(
    DOC_TEMPLATE_THEMES,
    {
      variables: { type: Doc_Template_Type_Enum_Enum.CmaReportTheme },
    },
  );
  useRefetchWhenMissing(
    data?.doc_template_theme_id,
    themeTemplatesData?.doc_templates,
    refetchThemeTemplates,
    setError,
  );

  const [updateCmaReport, updating] = useUpdateCmaReport(
    cmaReportId,
    'page-cover',
  );
  const [AddUserModalComponent, openAddUserModal] = useAddUserModal();

  const client = useApolloClient();

  const [updateLeadContact] = useMutation(UPDATE_LEAD_CONTACT);
  const [updateTeamBrandingColor] = useMutation(UPDATE_TEAM_BRANDING_COLOR_CMA);

  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const update = useCallback(
    async (formData?: Partial<StepCoverForm>) => {
      await updateCmaReport({
        report_title: formData?.report_title,
        broker_id: formData?.broker_id,
        contact_id: formData?.contact_id,
        font_family: formData?.font_family,
        doc_template_id: formData?.doc_template_id,
        doc_template_theme_id: formData?.doc_template_theme_id,
        language: formData?.language,
      });

      if (
        formData?.broker_id !== undefined &&
        formData.broker_id !== data?.broker_id
      ) {
        // Broker defaults are replaced by an AFTER UPDATE database trigger.
        await client
          .query({
            query: GET_CMA_REPORT_BROKER_SELECTIONS,
            variables: { id: cmaReportId },
            fetchPolicy: 'network-only',
          })
          .catch(setError);
      }

      if (formData && 'contact_id' in formData && data?.lead_id) {
        await updateLeadContact({
          variables: {
            leadId: data.lead_id,
            contactId: formData.contact_id,
          },
          update: (cache, { data: mutationData }) => {
            if (mutationData?.update_leads_by_pk) {
              const updatedLead = mutationData.update_leads_by_pk;
              cache.modify({
                id: cache.identify(updatedLead),
                fields: {
                  contact(_, { toReference }) {
                    return updatedLead.contact_id
                      ? toReference({
                          __typename: 'users',
                          id: updatedLead.contact_id,
                        })
                      : null;
                  },
                },
              });
            }
          },
        });
      }
    },
    [
      updateCmaReport,
      updateLeadContact,
      data?.lead_id,
      data?.broker_id,
      client,
      cmaReportId,
      setError,
    ],
  );

  const debouncedUpdate = useDebouncedHandler(300, update);

  const onChangeHandler: RaFormOnChange<StepCoverForm> = useCallback(
    (formData, name, type, { setValue }) => {
      if (type !== 'change') {
        return;
      }

      if (name === 'language' && formData.language) {
        searchParams.set('language', formData.language as string);
        setSearchParams(searchParams);

        const reportTitle =
          defaultTitles[formData.language as keyof typeof defaultTitles];

        updateCmaReport({
          report_title: reportTitle,
          language: formData.language,
        });

        setValue('report_title', reportTitle);
        return;
      }

      if (name === 'branding_color') {
        const teamId = data?.broker?.default_team?.id;
        if (teamId) {
          updateTeamBrandingColor({
            variables: {
              teamId,
              brandingColor: formData.branding_color,
              reportId: cmaReportId,
            },
          });
        }
        return;
      }

      debouncedUpdate(formData);
    },
    [
      cmaReportId,
      searchParams,
      setSearchParams,
      updateCmaReport,
      debouncedUpdate,
      data?.broker?.default_team?.id,
      updateTeamBrandingColor,
    ],
  );

  const satelliteImageUrl = useMemo(
    () =>
      getSatelliteImageUrl(data?.property?.lat ?? 0, data?.property?.lng ?? 0),
    [data?.property?.lat, data?.property?.lng],
  );

  const handleUpdateImage = useCallback(
    (newCoverImageId: string | null) => {
      updateCmaReport({
        cover_image_id: newCoverImageId,
      });
    },
    [updateCmaReport],
  );

  const cmaStepCoverFormDefinition: FormDefinitionType<StepCoverForm> =
    useCallback(
      ({ t }) => [
        {
          type: 'custom',
          name: 'settings_section',
          element: (
            <ReportSettingsSection
              expanded={settingsExpanded}
              onToggle={() => setSettingsExpanded(!settingsExpanded)}
              docTemplatesData={docTemplatesData?.doc_templates}
              themeTemplatesData={themeTemplatesData?.doc_templates}
              currentTemplateId={data?.doc_template?.id}
              currentThemeId={data?.doc_template_theme_id}
              currentThemeName={data?.doc_template_theme?.name}
              includeLanguage={true}
            />
          ),
          gridProps: { md: 12 },
        },
        {
          name: 'report_title',
          label: t('Report title'),
          type: 'text',
          gridProps: { md: 12 },
        },
        {
          name: 'contact_id',
          label: t('Report prepared for'),
          type: 'user',
          gridProps: { md: 12 },
          createUserSelected: async userData => {
            const newUser = await openAddUserModal({
              firstName: userData.first_name ?? undefined,
              lastName: userData.last_name ?? undefined,
              email: userData.email ?? undefined,
            });

            if (newUser) {
              await update({ contact_id: newUser.id });
              return newUser;
            }
          },
        },
        {
          name: 'broker_id',
          label: t('Report created by'),
          type: 'user',
          gridProps: { sm: 12, md: 12 },
        },
        {
          type: 'custom',
          name: 'cover',
          element: (
            <CoverPhotoSelector
              propertyImages={getFilteredPropertyImages(
                data?.property?.property_images,
              )}
              coverImageId={data?.cover_image?.id}
              propertyId={data?.property?.id ?? ''}
              type="cma_reports"
              onUpdate={handleUpdateImage}
              fragment={STEP_COVER_PHOTO_CMA_FRAGMENT}
              fragmentName="StepCoverPhotoCma"
              satelliteImageUrl={satelliteImageUrl}
            />
          ),
          gridProps: { md: 12 },
        },
      ],
      [
        settingsExpanded,
        docTemplatesData,
        themeTemplatesData,
        data,
        handleUpdateImage,
        satelliteImageUrl,
        openAddUserModal,
        update,
        setSettingsExpanded,
      ],
    );

  const onSubmit = useCallback(
    async (formData: StepCoverForm) => {
      await update(formData);
      props.setStep(props.step + 1);
    },
    [update, props],
  );

  if (!complete) {
    return (
      <Box sx={{ flexGrow: 1, position: 'relative', overflowY: 'auto', p: 2 }}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Stack
              key={`step-cover-skeleton-${index}`}
              sx={{
                gap: 1,
              }}
            >
              <Skeleton variant="rounded" height={20} width="30%" />
              <Skeleton variant="rounded" height={40} />
            </Stack>
          ))}
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <RaForm
        freezeInitialDefaultValues={true}
        formDefinition={cmaStepCoverFormDefinition}
        onSubmit={onSubmit}
        defaultValues={{
          report_title: data?.report_title ?? '',
          broker_id: data.broker?.id,
          contact_id: data.contact?.id,
          font_family: data?.font_family || 'default',
          doc_template_id: data?.doc_template?.id || '',
          doc_template_theme_id: data?.doc_template_theme_id ?? null,
          language: data?.language || 'en',
          branding_color: data?.broker?.default_team?.branding_color || null,
        }}
        onChange={onChangeHandler}
        contentScrollable
        actionButtonsComponent={
          <FooterActions {...props} hideBackButton updating={updating} />
        }
      />
      {AddUserModalComponent}
    </>
  );
};

export default StepCoverPhoto;
